// Consistent modal keyboard behavior.  Every modal in the app should call
// useModal({ onClose }) and spread the returned {containerProps} onto its
// root element.  Handles:
//
//   - Esc closes the modal (unless disabled)
//   - Autofocus first focusable element on mount
//   - Focus trap (Tab loops within the modal, Shift+Tab goes backward)
//   - Body scroll lock while modal is open
//   - Return focus to the previously-focused element on close
//
// Does NOT handle backdrop-click (leave that to caller — some modals want
// destructive-action guards on click-outside).  Does NOT handle Enter=submit
// (that's owned by <form onSubmit>).
//
// Usage:
//   const { containerProps } = useModal({ onClose: handleClose })
//   return (
//     <div className="fixed inset-0 z-50 …" onClick={handleClose}>
//       <div {...containerProps} className="…" onClick={e => e.stopPropagation()}>
//         …
//       </div>
//     </div>
//   )

import { useEffect, useRef } from 'react'

// Selector for focusable elements — matches the standard set used by
// screen readers + keyboard nav.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

export function useModal({ onClose, onSubmit, enabled = true, autoFocus = true, lockScroll = true, escToClose = true } = {}) {
  const containerRef = useRef(null)
  const previousActiveElement = useRef(null)

  useEffect(() => {
    if (!enabled) return

    // Remember what was focused BEFORE the modal opened so we can restore
    // on close.  Standard a11y pattern — keyboard-only users don't lose
    // their place.
    previousActiveElement.current = document.activeElement

    // Autofocus first focusable — timeout so the DOM is settled after mount.
    if (autoFocus) {
      const t = setTimeout(() => {
        const root = containerRef.current
        if (!root) return
        // Prefer an element flagged data-autofocus, else first focusable.
        const preferred = root.querySelector('[data-autofocus]')
        if (preferred && typeof preferred.focus === 'function') {
          preferred.focus()
          return
        }
        const first = root.querySelector(FOCUSABLE_SELECTOR)
        if (first && typeof first.focus === 'function') first.focus()
      }, 30)
      return () => clearTimeout(t)
    }
  }, [enabled, autoFocus])

  useEffect(() => {
    if (!enabled) return
    if (!lockScroll) return
    // Body scroll lock while modal is open — prevents the background page
    // from scrolling underneath.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [enabled, lockScroll])

  useEffect(() => {
    if (!enabled) return

    const handleKey = (e) => {
      // Esc closes
      if (escToClose && e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        onClose && onClose()
        return
      }
      // Cmd/Ctrl+Enter — submit the modal (universal "commit" shortcut).
      // Only fires when the modal registered an onSubmit; leaves plain
      // Enter to the browser's default (usually the form's submit button
      // or textarea newline).
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && onSubmit) {
        e.preventDefault()
        onSubmit()
        return
      }
      // Tab focus trap
      if (e.key === 'Tab' && containerRef.current) {
        const focusables = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
          .filter(el => el.offsetParent !== null) // visible only
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [enabled, escToClose, onClose, onSubmit])

  // Restore focus WHEN THE MODAL CLOSES (transition enabled=true → false),
  // not just on component unmount.  Most modals live inside a parent
  // component that stays mounted across open/close cycles — an unmount-
  // only cleanup would never fire in that lifecycle, and keyboard-only
  // users would land on <body> after Esc.
  useEffect(() => {
    if (enabled) return
    const prev = previousActiveElement.current
    if (prev && typeof prev.focus === 'function') {
      try { prev.focus() } catch { /* ignore */ }
    }
  }, [enabled])

  return {
    containerProps: {
      ref: containerRef,
      role: 'dialog',
      'aria-modal': 'true',
      tabIndex: -1,
    }
  }
}

export default useModal
